// ServerControlModal.js
// Read-only details + remote actions for a Dsync server instance.

import React, { useEffect, useState } from 'react';
import { Modal, Descriptions, Tag, Button, Space, Table, message, Popconfirm, Tooltip } from 'antd';
import { PauseCircleOutlined, PlayCircleOutlined, ReloadOutlined, RedoOutlined } from '@ant-design/icons';
import axios from '../../../config/axiosInstance';

const DSYNC_PRINCIPAL_HOST = 'dsync.lambdalogic.de';

const columnsEvents = [
    { title: 'When', dataIndex: 'at', key: 'at', render: (v) => (v ? new Date(v).toLocaleString() : '—'), width: 180 },
    { title: 'Type', dataIndex: 'type', key: 'type', render: (t) => <Tag>{t}</Tag>, width: 140 },
    { title: 'Observed Host', dataIndex: 'observedHost', key: 'observedHost', width: 220 },
    { title: 'Meta', dataIndex: 'meta', key: 'meta', render: (m) => (m ? <pre style={{ margin: 0 }}>{JSON.stringify(m, null, 2)}</pre> : '—') },
];

const columnsHosts = [
    { title: 'Host', dataIndex: 'host', key: 'host', width: 240 },
    { title: 'First Seen', dataIndex: 'firstSeenAt', key: 'firstSeenAt', width: 180 },
    { title: 'Last Seen', dataIndex: 'lastSeenAt', key: 'lastSeenAt', width: 180 },
    { title: 'Times', dataIndex: 'timesSeen', key: 'timesSeen', width: 80 },
];

const StatusPill = ({ online, status, paused }) => {
    if (paused || status === 'paused') return <Tag color="gold">PAUSED</Tag>;
    if (online || status === 'online') return <Tag color="green">ONLINE</Tag>;
    if (status === 'offline') return <Tag color="red">OFFLINE</Tag>;
    return <Tag>UNKNOWN</Tag>;
};

const ServerControlModal = ({ visible, onClose, server }) => {
    const [loading, setLoading] = useState(false);
    const [events, setEvents] = useState([]);
    const [agentFresh, setAgentFresh] = useState(null);

    const instanceId = server?.instanceId;

    const agent = agentFresh || server || {};
    const sys = agent?.system || {};
    const net = agent?.network || {};
    const geo = net?.geo || {};
    const systemHostnameShort = (sys?.hostname || '').split('.')?.[0] || null;

    // protected if it is the main host
    const isProtected = () => {
        const h = (agent?.lastObservedHost || '').toLowerCase().replace(/:\d+$/, '');
        return h === DSYNC_PRINCIPAL_HOST;
    };

    // Last seen = doc.lastSeenAt or the most recent event
    const lastSeen = React.useMemo(() => {
        const docTs = agent?.lastSeenAt ? new Date(agent.lastSeenAt).getTime() : 0;
        const evMax = events.length
            ? Math.max(...events.map(e => new Date(e.at || 0).getTime() || 0))
            : 0;
        const ts = Math.max(docTs, evMax);
        return ts ? new Date(ts).toLocaleString() : '—';
    }, [agent?.lastSeenAt, events]);

    // Observed Hosts calculated from events
    const observedHosts = React.useMemo(() => {
        const map = new Map();
        for (const ev of events) {
            const h = ev?.observedHost;
            if (!h) continue;
            const t = new Date(ev.at || 0).getTime() || 0;
            let entry = map.get(h);
            if (!entry) {
                entry = { host: h, firstTs: t, lastTs: t, timesSeen: 1 };
            } else {
                entry.firstTs = Math.min(entry.firstTs, t);
                entry.lastTs  = Math.max(entry.lastTs, t);
                entry.timesSeen += 1;
            }
            map.set(h, entry);
        }
        return Array.from(map.values())
            .sort((a,b) => b.lastTs - a.lastTs)
            .map(e => ({
                host: e.host,
                firstSeenAt: e.firstTs ? new Date(e.firstTs).toLocaleString() : null,
                lastSeenAt:  e.lastTs  ? new Date(e.lastTs ).toLocaleString() : null,
                timesSeen: e.timesSeen,
            }));
    }, [events]);

    const fetchEvents = async () => {
        if (!instanceId) return;
        setLoading(true);
        try {
            const { data } = await axios.get(`/control/agents/${instanceId}/events`);
            setEvents(data?.events || []);
        } catch {
            message.error('Error fetching server events');
        } finally {
            setLoading(false);
        }
    };

    const fetchAgentFresh = async () => {
        if (!instanceId) return;
        try {
            const { data } = await axios.get('/control/agents');
            const found = (data?.agents || []).find((a) => a.instanceId === instanceId) || null;
            setAgentFresh(found);
        } catch {

        }
    };

    useEffect(() => {
        if (!visible || !instanceId) return;
        fetchAgentFresh();
        fetchEvents();
    }, [visible, instanceId]);

    const doAction = async (action, body = {}) => {
        if (!instanceId) return;
        setLoading(true);
        try {
            await axios.post(`/control/agents/${instanceId}/${action}`, body);
            message.success(`${action.toUpperCase()} sent`);
            await Promise.all([fetchAgentFresh(), fetchEvents()]);
        } catch {
            message.error(`Failed to ${action}`);
        } finally {
            setLoading(false);
        }
    };

    const isPaused = !!agent?.paused || agent?.status === 'paused';
    const isOffline = agent?.status === 'offline';
    const locked = isProtected();
    const disabled = isOffline || locked;

    const control = (
        <Space>
            <Tooltip title="Refresh">
                <Button
                    icon={<ReloadOutlined />}
                    onClick={async () => {
                        await fetchAgentFresh();
                        await fetchEvents();
                    }}
                    loading={loading}
                />
            </Tooltip>

            {isPaused ? (
                <Tooltip title={locked ? 'Primary server cannot be resumed here' : 'Resume'}>
                    <Button
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        onClick={() => doAction('resume')}
                        loading={loading}
                        disabled={disabled}
                    >
                        Resume
                    </Button>
                </Tooltip>
            ) : (
                <Tooltip title={locked ? 'Primary server cannot be paused' : 'Pause'}>
                    <Button
                        style={{ color: '#fa8c16', borderColor: '#fa8c16', borderStyle: 'dashed' }}
                        icon={<PauseCircleOutlined />}
                        onClick={() => doAction('pause', { reason: 'manual' })}
                        loading={loading}
                        disabled={disabled}
                    >
                        Pause
                    </Button>
                </Tooltip>
            )}

            <Popconfirm
                title={locked ? 'Primary server cannot be restarted here' : 'Restart this server now?'}
                okText="Yes"
                cancelText="No"
                onConfirm={() => doAction('restart')}
                disabled={locked}
            >
                <Tooltip title={locked ? 'Primary server cannot be restarted' : 'Restart'}>
                    <Button icon={<RedoOutlined />} danger loading={loading} disabled={locked}>
                        Restart
                    </Button>
                </Tooltip>
            </Popconfirm>

            <Button onClick={onClose}>Close</Button>
        </Space>
    );

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            closable={false}
            title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <span>
            Server · {instanceId || ''}
              {locked && <Tag color="gold" style={{ marginLeft: 8 }}>PRIMARY</Tag>}
          </span>
                    <div>{control}</div>
                </div>
            }
            width={960}
            destroyOnClose
        >
            <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="Instance ID" span={2}>{instanceId}</Descriptions.Item>
                <Descriptions.Item label="Status">
                    <StatusPill online={agent?.online} status={agent?.status} paused={agent?.paused} />
                </Descriptions.Item>
                <Descriptions.Item label="Backend">
                    <Tag color="volcano">{agent?.backendVersion || 'n/a'}</Tag>
                </Descriptions.Item>

                <Descriptions.Item label="Last seen">{lastSeen}</Descriptions.Item>
                <Descriptions.Item label="Domain">
                    {agent?.lastObservedHost ? (
                        <Space size="small">
                            <Tag>{agent.lastObservedHost}</Tag>
                            {locked && <Tag color="geekblue">dsync</Tag>}
                        </Space>
                    ) : 'n/a'}
                </Descriptions.Item>

                <Descriptions.Item label="Host">
                    {systemHostnameShort ? <Tag color="blue">{systemHostnameShort}</Tag> : 'n/a'}
                </Descriptions.Item>
                <Descriptions.Item label="System">
                    <div>{sys.platform || '—'} {sys.arch || ''} · Node {sys.node || '—'}</div>
                </Descriptions.Item>

                <Descriptions.Item label="Public IP">
                    {net?.publicIp ? <Tag color="blue">{net.publicIp}</Tag> : 'n/a'}
                </Descriptions.Item>
                <Descriptions.Item label="Reverse DNS">
                    {net?.publicHostname || 'n/a'}
                </Descriptions.Item>

                <Descriptions.Item label="Geo" span={2}>
                    {geo?.country ? (
                        <span>
              {geo.city ? `${geo.city}, ` : ''}{geo.region ? `${geo.region}, ` : ''}{geo.country} ({geo.country_code})
                            {typeof geo.lat === 'number' && typeof geo.lon === 'number' ? ` · ${geo.lat}, ${geo.lon}` : ''}
                            {geo.org ? ` · ${geo.org}` : ''}
            </span>
                    ) : 'n/a'}
                </Descriptions.Item>
            </Descriptions>

            <div style={{ marginTop: 16 }}>
                <h3 style={{ marginBottom: 8 }}>Observed Hosts</h3>
                <Table
                    columns={columnsHosts}
                    dataSource={observedHosts}
                    rowKey={(r) => `${r.host}-${r.firstSeenAt || ''}-${r.lastSeenAt || ''}`}
                    size="small"
                    pagination={{ pageSize: 5 }}
                />
            </div>

            <div style={{ marginTop: 2 }}>
                <h3 style={{ marginBottom: 8 }}>Recent Events</h3>
                <Table
                    columns={columnsEvents}
                    dataSource={events}
                    rowKey={(r, i) => r.at || i}
                    size="small"
                    loading={loading}
                    pagination={{ pageSize: 8 }}
                    scroll={{ x: true }}
                />
            </div>
        </Modal>
    );
};

export default ServerControlModal;